import { type InjectedFormikProps } from "formik";
import { Component } from "react";

import { CheckAllState } from "types/enum";

import yup from "utilities/form/yup";
import { deepClone } from "utilities/object/object";
import { type KeyOfOrString } from "utilities/type/PropsOfType";

import { BTCheckbox } from "commonComponents/btWrappers/BTCheckbox/BTCheckbox";
import { BTPopover } from "commonComponents/btWrappers/BTPopover/BTPopover";
import { type IBTColumnProps } from "commonComponents/btWrappers/BTTable/BTTable";
import { BTTable } from "commonComponents/btWrappers/BTTable/BTTable";
import { getCheckAllState } from "commonComponents/btWrappers/BTTable/BTTable.utils";
import { BTTitle } from "commonComponents/btWrappers/BTTitle/BTTitle";
import { NotificationEmptyState } from "commonComponents/entity/notificationPreferences/NotificationEmptyState/NotificationEmptyState";
import {
    type INotificationPreferenceData,
    type INotificationPreferenceRow,
    type INotificationTypes,
} from "commonComponents/entity/notificationPreferences/NotificationPreferencesTable.api.types";

import {
    getCategoryKey,
    getSubCategoryKey,
    type InternalUserTabKeys,
} from "entity/internalUser/InternalUserDetails/InternalUser.api.types";
import {
    type IRolePermissionFormValues,
    type Role,
} from "entity/internalUser/RolePermissions/RolePermissions.api.types";

import "./NotificationPreferencesTable.less";

interface INotificationPreferencesTableFormValues {
    notificationPreferences: INotificationPreferenceRow[];
}
interface INotificationPreferencesTableProps {
    disabled?: boolean;
    hasRole?: boolean;
    onChangeActiveKey?: (value: InternalUserTabKeys) => void;
}

export class NotificationPreferencesTable extends Component<
    InjectedFormikProps<INotificationPreferencesTableProps, INotificationPreferencesTableFormValues>
> {
    static defaultProps = {
        hasRole: true,
    };

    private toggleAll(propName: KeyOfOrString<INotificationTypes>, isChecked: boolean) {
        const values = deepClone(this.props.values);
        values.notificationPreferences.forEach((pref) => {
            if (pref[propName] !== null) {
                pref[propName] = isChecked;
            }
        });
        this.props.setFieldValue("notificationPreferences", values.notificationPreferences);
    }

    private getCheckboxNode(
        value: boolean,
        record: INotificationPreferenceRow,
        index: number,
        propertyName: KeyOfOrString<INotificationTypes>,
    ): React.ReactNode {
        let alwaysOnKey: keyof INotificationPreferenceRow | undefined;
        let channelName: string | undefined;
        switch (propertyName) {
            case "emailEnabled":
                alwaysOnKey = "alwaysOnEmail";
                channelName = "email";
                break;
            case "textEnabled":
                alwaysOnKey = "alwaysOnText";
                channelName = "text";
                break;
            case "pushEnabled":
                alwaysOnKey = "alwaysOnPush";
                channelName = "push";
                break;
            default:
                alwaysOnKey = undefined;
                channelName = undefined;
        }

        if (alwaysOnKey && record[alwaysOnKey]) {
            return (
                <BTPopover
                    content={`${record.name} notifications via ${channelName!} are always enabled`}
                    placement="top"
                >
                    <BTCheckbox
                        id={`notificationPreferences[${record.index!}].${propertyName}.alwaysOn`}
                        data-testid={`notificationPreferences[${record.index!}].${propertyName}.alwaysOn`}
                        checked={true}
                        disabled={true}
                    />
                </BTPopover>
            );
        }
        if (value !== null) {
            return (
                <BTCheckbox
                    id={`notificationPreferences[${record.index!}].${propertyName}`}
                    data-testid={propertyName}
                    checked={value}
                    onChange={this.props.handleChange}
                    disabled={this.props.disabled}
                />
            );
        }
        return null;
    }

    render() {
        const { values, onChangeActiveKey } = this.props;

        // If the user has no role selected, show the empty state instead
        if (!this.props.hasRole) {
            return <NotificationEmptyState onChangeActiveKey={onChangeActiveKey} />;
        }

        const emailEnabledCheckedState = getCheckAllState(
            values.notificationPreferences,
            "emailEnabled",
        );
        const textEnabledCheckedState = getCheckAllState(
            values.notificationPreferences,
            "textEnabled",
        );
        const pushEnabledCheckedState = getCheckAllState(
            values.notificationPreferences,
            "pushEnabled",
        );
        const allUsersEnabledCheckedState = getCheckAllState(
            values.notificationPreferences,
            "allUsersEnabled",
        );

        const columnProps: IBTColumnProps<INotificationPreferenceRow>[] = [
            {
                title: "",
                dataIndex: "groupName",
                render: (_, record) => {
                    if (record.isCategoryRow) {
                        return {
                            children: (
                                <BTTitle level={4} bold>
                                    {record.groupName}
                                </BTTitle>
                            ),
                        };
                    } else {
                        return record.groupName;
                    }
                },
            },
            {
                title: "",
                dataIndex: "name",
                render: (_, record) => {
                    return record.name;
                },
            },
            {
                title: (
                    <>
                        Email
                        <BTCheckbox
                            id="selectAllEmail"
                            data-testid="selectAllEmail"
                            checked={emailEnabledCheckedState === CheckAllState.AllChecked}
                            indeterminate={emailEnabledCheckedState === CheckAllState.Indeterminate}
                            onChange={(e) => this.toggleAll("emailEnabled", e.target.checked)}
                            className="CheckboxWithTitle"
                            disabled={this.props.disabled}
                        />
                    </>
                ),
                dataIndex: "emailEnabled",
                align: "center",
                render: (value, record, index) =>
                    this.getCheckboxNode(value, record, index, "emailEnabled"),
                className: "NotificationPreferencesTable-CheckboxColumn",
            },
            {
                title: (
                    <>
                        Text
                        <BTCheckbox
                            id="selectAllText"
                            data-testid="selectAllText"
                            checked={textEnabledCheckedState === CheckAllState.AllChecked}
                            indeterminate={textEnabledCheckedState === CheckAllState.Indeterminate}
                            onChange={(e) => this.toggleAll("textEnabled", e.target.checked)}
                            className="CheckboxWithTitle"
                            disabled={this.props.disabled}
                        />
                    </>
                ),
                align: "center",
                dataIndex: "textEnabled",
                render: (value, record, index) =>
                    this.getCheckboxNode(value, record, index, "textEnabled"),
                className: "NotificationPreferencesTable-CheckboxColumn",
            },
            {
                title: (
                    <>
                        Push
                        <BTCheckbox
                            id="selectAllPush"
                            data-testid="selectAllPush"
                            checked={pushEnabledCheckedState === CheckAllState.AllChecked}
                            indeterminate={pushEnabledCheckedState === CheckAllState.Indeterminate}
                            onChange={(e) => this.toggleAll("pushEnabled", e.target.checked)}
                            className="CheckboxWithTitle"
                            disabled={this.props.disabled}
                        />
                    </>
                ),
                align: "center",
                dataIndex: "pushEnabled",
                render: (value, record, index) =>
                    this.getCheckboxNode(value, record, index, "pushEnabled"),
                className: "NotificationPreferencesTable-CheckboxColumn",
            },
        ];
        if (values.notificationPreferences.some((pref) => pref.allUsersEnabled !== null)) {
            columnProps.push({
                title: (
                    <>
                        All Users
                        <BTCheckbox
                            id="selectAllUsers"
                            data-testid="selectAllUsers"
                            checked={allUsersEnabledCheckedState === CheckAllState.AllChecked}
                            indeterminate={
                                allUsersEnabledCheckedState === CheckAllState.Indeterminate
                            }
                            onChange={(e) => this.toggleAll("allUsersEnabled", e.target.checked)}
                            className="CheckboxWithTitle"
                            disabled={this.props.disabled}
                        />
                    </>
                ),
                align: "center",
                dataIndex: "allUsersEnabled",
                render: (value, record, index) =>
                    this.getCheckboxNode(value, record, index, "allUsersEnabled"),
                className: "NotificationPreferencesTable-CheckboxColumn",
            });
        }

        return (
            <BTTable
                dataSource={values.notificationPreferences.filter((p) => p.canView)}
                columns={columnProps}
                rowKey={(record) =>
                    record.isCategoryRow ? record.groupName!.toString() : record.type!.toString()
                }
                pagination={false}
                rowClassName={(record) =>
                    record.isCategoryRow ? "NotificationPreferencesTable-category-row" : ""
                }
                className="SelectAllRowsTable"
            />
        );
    }
}

export const setRoleViewPermissions = (
    notificationPrefs: INotificationPreferenceRow[],
    role: IRolePermissionFormValues,
) => {
    const categoryRows: boolean[] = [];
    let categoryName = "";
    let groupName = "";
    let i = -1;
    const notificationPrefsCopy = deepClone(notificationPrefs);

    notificationPrefsCopy.map((p) => {
        if (p.isCategoryRow && p.groupName) {
            categoryName = p.groupName;
            i++;
            return p;
        }

        if (!p.isCategoryRow && p.groupName) {
            groupName = p.groupName;
        }

        if (role !== undefined) {
            p.canView =
                p.canView &&
                role.categories[getCategoryKey(categoryName)].subCategories[
                    getSubCategoryKey(groupName)
                ].permissions.view.enabled;

            if (!p.isCategoryRow && p.groupName) {
                categoryRows[i] = categoryRows[i] || p.canView;
            }
        }

        return p;
    });

    // We need to check if Categories have any visible Groups beneath them
    const categories = notificationPrefsCopy.filter((np) => np.isCategoryRow);

    for (let j = 0; j < categories.length; j++) {
        categories[j].canView = categoryRows[j];
    }

    return notificationPrefsCopy;
};

export const mapPreferencesToRows = (data: INotificationPreferenceData, role?: Role) => {
    const rowValues: INotificationPreferenceRow[] = [];
    let newIndex = 0;
    data.categories.forEach((category) => {
        category.groups.forEach((group, groupIndex) => {
            if (groupIndex === 0 && category.category !== null) {
                rowValues.push({
                    groupName: category.category,
                    name: "",
                    emailEnabled: null,
                    textEnabled: null,
                    pushEnabled: null,
                    allUsersEnabled: null,
                    isCategoryRow: true,
                    isGroupRow: false,
                    canView: true,
                    canViewAllUserActivity: true,
                    parent: null,
                    index: newIndex,
                });

                newIndex++;
            }
            group.preferences.forEach((pref, index) => {
                rowValues.push({
                    groupName: index === 0 ? group.name : "",
                    name: pref.name,
                    emailEnabled: pref.emailEnabled,
                    textEnabled: pref.textEnabled,
                    pushEnabled: pref.pushEnabled,
                    allUsersEnabled: pref.allUsersEnabled,
                    tab: group.tabType,
                    type: pref.type,
                    isCategoryRow: false,
                    isGroupRow: false,
                    canView: pref.canView ?? true,
                    canViewAllUserActivity: true,
                    parent: null,
                    index: newIndex,
                    tooltipText: pref.tooltipText,
                    alwaysOnEmail: pref.alwaysOnEmail,
                    alwaysOnText: pref.alwaysOnText,
                    alwaysOnPush: pref.alwaysOnPush,
                });

                newIndex++;
            });
        });
    });
    if (role) {
        return setRoleViewPermissions(rowValues, role);
    }
    return rowValues;
};

export const NotificationPreferencesTableValidators = yup
    .array()
    .label("Notification preferences")
    .of(
        yup.object().shape<INotificationPreferenceRow>({
            groupName: yup.string().nullable(true).label("Group"),
            name: yup.string().label("Preference"),
            emailEnabled: yup.bool().nullable(true).label("Email"),
            textEnabled: yup.bool().nullable(true).label("Text"),
            pushEnabled: yup.bool().nullable(true).label("Push"),
            allUsersEnabled: yup.bool().nullable(true).label("All Users Enabled"),
            tab: yup.number().label("Tab"),
            type: yup.number().label("Type"),
            isCategoryRow: yup.bool().label("Category Row"),
            isGroupRow: yup.bool().label("Group Row"),
            canView: yup.bool().label("Can View"),
            canViewAllUserActivity: yup.bool().label("Can View All User Permission"),
            parent: yup.string().nullable(true).label("Parent"),
            index: yup.number().nullable(true).label("Index"),
        }),
    );
